import { useEffect, useState } from "react";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";
import MessagePreview from "../components/MessagePreview";
import { Btn, Card, Collapsible, Field, Input, PasswordInput, Textarea, Toggle, SwitchRow,
         G_DARK } from "../components/Shared";

// ── Notifications tab ────────────────────────────────────────────────────────

const NOTIFICATION_TYPE_LABELS = {
  submission:      "Submission Received",
  results:         "Results Ready",
  account_created: "User Account Created",
  newsletter:      "Project Summary",
  test:            "Test Email",
  recovery:        "Account Recovery",
  // The two signing mails, each the template of its own kind (2026-11-03): a
  // Statement of Work is never mailed from the quotation's text.
  quotation:       "Quotation to Signatories",
  sow:             "Statement of Work to Signatories",
  storage_warning: "Storage Nearly Full",
  closeout:        "Close-out Record",
  profile_confirmed: "Profile Confirmed",
  policy_reminder: "Policy Waiting for Confirmation",
  approval_request: "Policy Waiting for Approval",
};

// The five whose wording lived in code until 2026-09-23. They are templates
// now, but **without an enabled switch**: recovery, the signing mail, the
// storage warning and the close-out record must go out whenever their occasion
// arises, and the test mail is only ever sent on demand. The profile-confirmed
// mail joined them on 2026-09-28: it is how a self-registered person learns
// their account works. The policy reminder (2026-09-29) goes only when a group
// admin presses Send notification, so a switch would switch nothing, and the
// approval ask (2026-09-30) only on Ask for approval. Mirrors
// `ALWAYS_SENT_KINDS` in services/notifications.py.
const ALWAYS_SENT = [
  { type: "test",            when: "Sent when an admin presses Send test email." },
  { type: "recovery",        when: "Sent when someone uses Forgot your password?" },
  { type: "quotation",       when: "Sent when a quotation is sent, or a reminder is sent to its signatories." },
  { type: "sow",             when: "Sent when a Statement of Work is sent, or a reminder is sent to its signatories. A Statement of Work uses only this one." },
  { type: "storage_warning", when: "Sent to the admins when a bucket passes the warning threshold." },
  { type: "closeout",        when: "Sent when an order ends and the record goes to the invoicing address." },
  { type: "profile_confirmed", when: "Sent when an operator confirms a self-registered person's profile." },
  { type: "policy_reminder", when: "Sent when a group admin presses Send notification on a policy document, to each person ticked who has not confirmed it yet." },
  { type: "approval_request", when: "Sent when someone presses Ask for approval on a policy document, to each administrator ticked who may approve it — never to whoever asked for it." },
];

function SmtpCard({ settings, onSaved, meEmail }) {
  const [form, setForm] = useState({
    smtp_host: settings.smtp_host,
    smtp_port: settings.smtp_port,
    smtp_user: settings.smtp_user,
    smtp_password: "",
    smtp_from: settings.smtp_from,
    smtp_reply_to: settings.smtp_reply_to ?? "",
    // The stored values, as the backend reports them — `smtp_secure` there is
    // already derived from the port while nobody has set it. Deriving it again
    // here on mount would show, and then save, a deliberate override as undone.
    smtp_secure: settings.smtp_secure ?? Number(settings.smtp_port) === 465,
    smtp_verify_cert: settings.smtp_verify_cert ?? true,
  });
  const [busy, setBusy]   = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const [testRecipient, setTestRecipient] = useState("");
  const [testing, setTesting]             = useState(false);
  const [testResult, setTestResult]       = useState(null);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  // Changing the port moves Secure with it — 465 is implicit TLS, 587 and 25
  // are STARTTLS — and the switch can still be flipped afterwards. Only a
  // change of the port does this, never the card opening.
  const setPort = e => {
    const value = e.target.value;
    setForm(f => ({ ...f, smtp_port: value, smtp_secure: Number(value) === 465 }));
  };

  const handleSave = async () => {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const payload = { ...form, smtp_port: Number(form.smtp_port) };
      if (!payload.smtp_password) delete payload.smtp_password;
      const result = await api.patch("/api/admin/notification-settings", payload);
      onSaved(result);
      setForm(f => ({ ...f, smtp_password: "" }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await api.post("/api/admin/notification-settings/test-email", { recipient: testRecipient || undefined });
      setTestResult(result);
    } catch (e) {
      setTestResult({ sent: false, error: e.message });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card style={{ marginBottom: 20 }}>
      <h3 style={{ margin: "0 0 20px", fontSize: 15, fontWeight: 700 }}>SMTP Connection</h3>
      {error && (
        <div role="alert" style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>
          {error}
        </div>
      )}
      <div data-col2 style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        <Field label="Host"><Input value={form.smtp_host} onChange={set("smtp_host")} placeholder="smtp.example.com" /></Field>
        <Field label="Port"><Input type="number" value={form.smtp_port} onChange={setPort} /></Field>
        <Field label="Username"><Input value={form.smtp_user} onChange={set("smtp_user")} /></Field>
        <Field label="Password" hint={settings.smtp_password_set ? "Currently set — leave blank to keep it" : "Not set"}>
          <PasswordInput value={form.smtp_password} onChange={set("smtp_password")} placeholder="••••••••" />
        </Field>
        <Field label="From address"><Input value={form.smtp_from} onChange={set("smtp_from")} placeholder="noreply@boku.ac.at" /></Field>
        <Field label="Reply-to address (optional)" hint="Where a reply goes. Leave blank for no Reply-To header.">
          <Input value={form.smtp_reply_to} onChange={set("smtp_reply_to")} placeholder="facility@boku.ac.at" />
        </Field>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
        <SwitchRow
          label="Secure (TLS)"
          checked={form.smtp_secure}
          onChange={v => setForm(f => ({ ...f, smtp_secure: v }))}
          hint="On: TLS from the first byte, as port 465 expects. Off: STARTTLS, as 587 and 25 expect. Follows the port; can be overridden."
        />
        <SwitchRow
          label="Verify SSL certificate"
          checked={form.smtp_verify_cert}
          onChange={v => setForm(f => ({ ...f, smtp_verify_cert: v }))}
          hint="Turn off only for a relay with a self-signed certificate. The connection stays encrypted but the server is not authenticated."
        />
      </div>
      <Btn onClick={handleSave} disabled={busy}>{busy ? "Saving…" : saved ? "✓ Saved" : "Save"}</Btn>

      <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid #f3f4f6" }}>
        <Field label="Send test email" hint="Verify the settings above actually work, without waiting for a real notification.">
          <div data-field-row style={{ display: "flex", gap: 8 }}>
            <Input
              value={testRecipient}
              onChange={e => setTestRecipient(e.target.value)}
              placeholder={meEmail || "recipient@example.com"}
              style={{ maxWidth: 280 }}
            />
            <Btn variant="secondary" onClick={handleTest} disabled={testing}>{testing ? "Sending…" : "Send test email"}</Btn>
          </div>
        </Field>
        {testResult && (
          <div style={{ marginTop: 10, fontSize: 13, color: testResult.sent ? G_DARK : "#dc2626" }}>
            {testResult.sent ? "✓ Test email sent successfully." : `✗ Failed: ${testResult.error}`}
          </div>
        )}
      </div>
    </Card>
  );
}

// `alwaysSent`: one of the five with no enabled switch — no toggle, no
// `*_enabled` in the payload (the schema has none for them), and a one-line
// `when` under the heading.
//
// Each template is a section, closed on load (2026-09-30): eleven open cards,
// each a seven-row body and a preview, were 14 000 px of scroll on a phone.
// A closed section renders nothing, so no preview is requested for a template
// nobody opened. The draft lives in this component's state, *above* the
// Collapsible, so closing a section does not discard a text typed and not yet
// saved — keep it there. The header carries the *stored* on/off, which is what
// is actually sent.
function TemplateCard({ type, data, onSaved, alwaysSent = false, when }) {
  const [enabled, setEnabled] = useState(data.enabled);
  const [subject, setSubject] = useState(data.subject);
  const [body, setBody]       = useState(data.body);
  const [busy, setBusy]       = useState(false);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState(null);
  const [debounced, setDebounced] = useState({ subject: data.subject, body: data.body });

  useEffect(() => {
    const t = setTimeout(() => setDebounced({ subject, body }), 400);
    return () => clearTimeout(t);
  }, [subject, body]);

  const handleSave = async () => {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      // **Only what was actually changed.** Sending the text on every Save
      // meant that merely switching a notification on stored its subject and
      // body as settings rows — and the backend reads them as
      // `get_setting(key, DEFAULT)`, so from that moment the application's own
      // wording never reached this deployment again. Nothing seeds those rows,
      // and nothing on this screen shows that one exists: the stored value and
      // the built-in default look identical here.
      // The comparison is against what the server last sent us, which is also
      // what is on screen, so typing a change and undoing it sends nothing —
      // and putting the facility's own wording back does send it, which is how
      // the row gets deleted again.
      const payload = alwaysSent ? {} : { [`${type}_enabled`]: enabled };
      if (subject !== data.subject) payload[`${type}_subject`] = subject;
      if (body !== data.body)       payload[`${type}_body`]    = body;
      const result = await api.patch("/api/admin/notification-settings", payload);
      onSaved(result);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Collapsible title={NOTIFICATION_TYPE_LABELS[type]}
                 subtitle={alwaysSent ? null : (data.enabled ? "Enabled" : "Disabled")}>
      {!alwaysSent && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <Toggle checked={enabled} onChange={setEnabled}
                  label={`Send ${NOTIFICATION_TYPE_LABELS[type]}`} />
          <span style={{ fontSize: 12, color: "#6b7280" }}>{enabled ? "Enabled" : "Disabled"}</span>
        </div>
      )}
      {when && <p style={{ margin: "0 0 16px", fontSize: 12, color: "#6b7280" }}>{when}</p>}
      {error && (
        <div role="alert" style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>
          {error}
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Field label="Subject">
          <Input value={subject} onChange={e => setSubject(e.target.value)} />
        </Field>
        <Field
          label="Body"
          /* `?? []`, because an undefined.map() here does not fail the field —
             it unmounts the whole page. That is the blank Settings page of
             2026-08-12, recorded a few hundred lines above, and this is the
             same shape: a template the server described without a `variables`
             key. The placeholder hint is worth having and is not worth the
             screen. */
          hint={`Available placeholders: ${(data.variables ?? []).map(v => `{{${v}}}`).join(", ")}`
            + (alwaysSent
              // The quotation's list spans two fields ({{sign_link}} in the
              // body, {{quotation_number}} in the subject); the server's
              // refusal names which one a save is missing.
              ? (data.required_variables?.length
                ? `. Required: ${data.required_variables.map(v => `{{${v}}}`).join(", ")} — a text without it is refused.`
                : ". Nothing is required.")
              : data.required_variables?.length
              // Saying so up front beats finding out via a rejected save —
              // the backend refuses a body without it, because the recipient
              // would otherwise have no way into the account.
              ? `. ${(data.required_variables ?? []).map(v => `{{${v}}}`).join(", ")} is required.`
              : "")}
        >
          <Textarea rows={7} value={body} onChange={e => setBody(e.target.value)} />
        </Field>
        {/* Previews the text as typed, not as stored, so the effect of an edit
            is visible before saving. Debounced so a keystroke isn't a request.
            One of the five, untouched, is rendered instead by the function that
            sends it (`account-email-preview`), so what is shown before anyone
            types is the mail as it would go out now, not a sample-value copy. */}
        <MessagePreview
          title="How this will arrive"
          deps={[type, debounced.subject, debounced.body, data.subject, data.body]}
          fetcher={() => (alwaysSent && debounced.subject === data.subject && debounced.body === data.body
            ? api.get(`/api/admin/account-email-preview?kind=${type}`)
            : api.post("/api/admin/notification-settings/preview",
              { type, subject: debounced.subject, body: debounced.body }))}
        />
      </div>
      <div style={{ marginTop: 16 }}>
        <Btn onClick={handleSave} disabled={busy}>{busy ? "Saving…" : saved ? "✓ Saved" : "Save"}</Btn>
      </div>
    </Collapsible>
  );
}

export default function NotificationsTab() {
  const { user: me }            = useAuth();
  const [settings, setSettings] = useState(null);
  const [error, setError]       = useState(null);
  // Whether Keycloak sign-in is on, read beside the templates. Only the note
  // about Keycloak's own mails hangs on it, so a failed read leaves the tab as
  // it is and the note away — it is never the tab's error.
  const [oidcEnabled, setOidcEnabled] = useState(false);

  useEffect(() => {
    api.get("/api/admin/notification-settings")
      .then(setSettings)
      .catch(e => setError(e.message));
    api.get("/api/admin/auth-settings")
      .then(s => setOidcEnabled(s?.oidc_enabled === true))
      .catch(() => setOidcEnabled(false));
  }, []);

  if (error) {
    return (
      <div role="alert" style={{ padding: "12px 16px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>
        {error}
      </div>
    );
  }
  if (!settings) return <div style={{ textAlign: "center", padding: "48px 0", color: "#6b7280" }}>Loading…</div>;

  return (
    <div style={{ maxWidth: 640 }}>
      <SmtpCard settings={settings} onSaved={setSettings} meEmail={me?.email} />
      {oidcEnabled && <KeycloakMailsNote />}
      <TemplateCard type="submission" data={settings.submission} onSaved={setSettings} />
      <TemplateCard type="results" data={settings.results} onSaved={setSettings} />
      <TemplateCard type="account_created" data={settings.account_created} onSaved={setSettings} />
      {/* Off by default and separately from anybody's subscription: turning it
          on here is what lets the summary job send at all, and how often each
          subscriber gets one is theirs alone. */}
      <TemplateCard type="newsletter" data={settings.newsletter} onSaved={setSettings} />
      <AlwaysSentIntro />
      {ALWAYS_SENT.filter(({ type }) => settings[type]).map(({ type, when }) => (
        <TemplateCard key={type} type={type} data={settings[type]} onSaved={setSettings}
                      alwaysSent when={when} />
      ))}
    </div>
  );
}

// The heading over the cards that have no switch. What used to be the
// read-only "Fixed Messages" card: their wording was code until 2026-09-23.
function AlwaysSentIntro() {
  return (
    <div style={{ margin: "8px 0 16px" }}>
      <h3 style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 700 }}>Messages that always go out</h3>
      <p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}>
        These have no switch: each is sent whenever its occasion arises — the test mail and the
        policy reminder when somebody presses the button for them. Their subject and text
        can be reworded like the templates above, but every link and every block the portal computes
        is a placeholder a text cannot drop — a save without a required one is refused. Links are
        never shown here: a preview puts a placeholder where the working link would be. Putting
        the built-in text back makes it the default again, so later releases' wording arrives.
      </p>
    </div>
  );
}

// 2026-10-06: an admin looked here for the account confirmation after a
// Keycloak self-registration. Keycloak sends that mail, not the portal, so no
// template here can reword it. Shown only while Keycloak sign-in is on.
function KeycloakMailsNote() {
  return (
    <p style={{ margin: "0 0 16px", padding: "10px 14px", borderRadius: 8, fontSize: 12,
                color: "#374151", background: "rgba(37,99,235,0.05)",
                border: "1px solid rgba(37,99,235,0.2)" }}>
      Keycloak sign-in is on, so Keycloak sends its own mails — verify e-mail after a
      self-registration and reset credentials among them. They are not templates here: their text
      is edited in the Keycloak realm's e-mail theme or localisation.
    </p>
  );
}
